import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { IAccount } from 'src/app/acct-comps/accounts.interfaces';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { Observable, combineLatest } from 'rxjs';
import { filter as rxFilter, take } from 'rxjs/operators';
import { IPublish, IReviewData, IReviewMulti } from '../../review-management.interfaces';
import { ConfirmationPopupComponent } from '../confirmation-popup/confirmation-popup.component';

@Component({
  selector: 'app-multi-reply',
  templateUrl: './multi-reply.component.html',
  styleUrls: ['./multi-reply.component.scss'],
})
export class MultiReplyComponent implements OnInit {
  @Input() selectedReviews: IReviewMulti[] = [];
  accountId: number;
  loginId: number;
  @Output() closeDialog: EventEmitter<boolean | { publishObject: IPublish<IReviewMulti[]>; confirmed: boolean }> = new EventEmitter();
  selectedReview: IReviewMulti;
  replyText = '';
  updatedReviwes: IReviewData[];
  constructor(private dialogService: DialogService, private sessionService: SessionService) {}

  /**
   * Initializes selected review state and loads authenticated account/user identifiers.
   * Fetches account and user once and stores `accountId` and `loginId` for publish calls.
   * @returns None.
   */
  ngOnInit(): void {
    this.selectedReview = this.selectedReviews[0];
    this.selectedReview.pos = 1;
    this.initUserFetchData().subscribe(([account, user]: [IAccount, any]) => {
      this.accountId = account?._id;
      this.loginId = user?.login?._id;
    });
  }

  /**
   * Fetches selected account and current user once for id initialization.
   * Emits a tuple with account and user data for downstream id assignment.
   * @returns Observable containing `[account, user]`.
   */
  private initUserFetchData(): Observable<[IAccount, any]> {
    return combineLatest([
      this.sessionService.getSelectedAccount$().pipe(rxFilter(Boolean), take(1)) as Observable<IAccount>,
      this.sessionService.getCurrentUser$().pipe(rxFilter(Boolean), take(1)),
    ]) as Observable<[IAccount, any]>;
  }

  switchCard(step: -1 | 1) {
    const idx = this.selectedReviews.indexOf(this.selectedReview);
    if (idx == 0 && step == -1) {
      this.selectedReview = this.selectedReviews[this.selectedReviews.length - 1];
      this.selectedReview.pos = this.selectedReviews.length;
    } else {
      this.selectedReview = this.selectedReviews[(idx + step) % this.selectedReviews.length];
      this.selectedReview.pos = ((idx + step) % this.selectedReviews.length) + 1;
    }
  }

  cancel() {
    this.closeDialog.emit(false);
  }

  /**
   * Builds the publish payload for selected reviews and opens the confirmation dialog.
   * Emits the publish payload and confirmation result when the confirmation dialog closes.
   * @returns None.
   */
  confirmReply(): void {
    const type = this.selectedReviews.length == 1 ? 'single' : 'multi';
    this.updatedReviwes = this.selectedReviews.map((review) => {
      return {
        ...review,
        replyComment: this.replyText,
      };
    });
    const publishObject: IPublish<IReviewMulti[]> & { accountId: number; loginId: number } = {
      type,
      data: this.updatedReviwes,
      accountId: this.accountId,
      loginId: this.loginId,
    };
    this.dialogService
      .open(ConfirmationPopupComponent, {
        data: { ...publishObject, userId: this.loginId, accountId: this.accountId },
        closable: false,
        width: '800px',
        height: '300px',
      })
      .onClose.subscribe((confirmed) => {
        this.closeDialog.emit({ publishObject, confirmed });
      });
  }
}
